import { Dexie } from 'dexie';
import { StorageError } from 'lib/errors';
import { isError } from 'lib/isError';
import { Engine } from 'lib/storage/types';

const cleanFilename = (key: string): string =>
  key.replace(/[^a-z0-9.\-_/]/gi, '-');

const getDirectoryFilename = ({
  filename,
  directory,
}: {
  filename: string;
  directory?: string;
}): string =>
  directory
    ? `${directory}/${cleanFilename(filename)}`
    : cleanFilename(filename);

const dbName = 'riskbase';
const db = new Dexie(dbName);
db.version(1).stores({
  riskbase: 'directory_filename, directory',
});

const getBase64Filename = (key: string) => `${key}-b64`;

export const storage: Engine = {
  writeJsonFile: async ({ filename, data: value, directory }) => {
    try {
      await db.table(dbName).put({
        directory: directory,
        directory_filename: directory ? `${directory}/${filename}` : filename,
        value,
      });
    } catch (error) {
      throw new StorageError(
        `Unable to save item to storage (${
          isError(error) ? error.message : 'No Error'
        })`,
      );
    }
  },

  readJsonFile: async ({ filename, directory }) => {
    try {
      const item = await db.table(dbName).get({
        directory_filename: getDirectoryFilename({ filename, directory }),
      });
      if (!item) {
        throw new Error('Item not found');
      }
      return item.value;
    } catch (error) {
      throw new StorageError(
        `Unable to get item from storage (${
          isError(error) ? error.message : 'No Error'
        })`,
      );
    }
  },

  writeStringFile: async ({ filename, directory, data }) => {
    try {
      await db.table(dbName).put({
        directory: directory || '',
        directory_filename: getDirectoryFilename({
          filename,
          directory,
        }),
        value: data,
      });
    } catch (error) {
      throw new StorageError(
        `Unable to save item to storage (${
          isError(error) ? error.message : 'No Error'
        })`,
      );
    }
  },

  readStringFile: async ({ filename, directory }) => {
    return storage.readJsonFile({ filename, directory });
  },

  writeBase64File: async ({ filename, data, directory }) => {
    try {
      await db.table(dbName).put({
        directory: directory || '',
        directory_filename: getDirectoryFilename({
          filename: getBase64Filename(filename),
          directory,
        }),
        value: data,
      });
    } catch (error) {
      throw new StorageError(
        `Unable to save item to storage (${
          isError(error) ? error.message : 'No Error'
        })`,
      );
    }
  },

  readBase64File: async ({ filename, directory }) => {
    return storage.readJsonFile({
      filename: getBase64Filename(filename),
      directory,
    });
  },

  getBase64Inline: async ({ filename, directory, base64Prefix }) => {
    return (
      base64Prefix +
      (await storage.readJsonFile({
        filename: getBase64Filename(filename),
        directory,
      }))
    );
  },

  deleteBase64File: async ({ filename, directory }) => {
    try {
      await db.table(dbName).delete(
        getDirectoryFilename({
          filename: getBase64Filename(filename),
          directory,
        }),
      );
    } catch (error) {
      throw new StorageError(
        `Unable to remove item from storage (${
          isError(error) ? error.message : 'No Error'
        })`,
      );
    }
  },

  deleteFile: async ({ filename, directory }) => {
    try {
      await db
        .table(dbName)
        .delete(getDirectoryFilename({ filename, directory }));
    } catch (error) {
      throw new StorageError(
        `Unable to remove item from storage (${
          isError(error) ? error.message : 'No Error'
        })`,
      );
    }
  },

  deleteDirectory: async (directory) => {
    try {
      await db.table(dbName).where({ directory }).delete();
    } catch (error) {
      throw new StorageError(
        `Unable to remove directory from storage (${
          isError(error) ? error.message : 'No Error'
        })`,
      );
    }
  },

  deleteAll: async () => {
    try {
      await db.table(dbName).clear();
    } catch (error) {
      throw new StorageError(
        `Unable to clear storage (${
          isError(error) ? error.message : 'No Error'
        })`,
      );
    }
  },
};
